// The tasting a person is in the middle of, as plain data and the pure steps that change it: which
// wines are on the table, which one is in front of the taster, what each draft holds and which are
// saved. Nothing here touches the network or storage (store.ts persists it, actions.ts talks to
// the edge), so every rule about "n of X" and "the next wine to taste" is testable on its own.
import type { FlightState, WineIdentity } from "@proef/contract";
import { estimateHue } from "@proef/domain";
import { emptyDraft, type NoteDepth, type NoteDraft } from "../noteDraft";
import { withLook, type WineLook } from "./wineLook";

/** What this device remembers of a wine's colour, by wine id (see wineLook.ts). */
export type RecallLook = (wineId: string) => WineLook | undefined;

/** The guided pager's pages; a quick tasting is one page and has no steps. */
export const FULL_STEPS = ["look", "smell", "taste", "structure", "summary"] as const;
export type FullStep = (typeof FULL_STEPS)[number];

export type WineStatus = "done" | "tasting" | "todo";

export interface RunWine {
  /** Stable on this device; the wine's place in the list is not (a wine can be removed). */
  readonly key: string;
  /** The bottle, when the taster may know it; a blind pour has none until the reveal. */
  readonly wine?: WineIdentity | undefined;
  readonly blind: boolean;
  /** The wine's id inside the flight once the tasting is shared. */
  readonly flightWineId?: string | undefined;
  /** The journal entry a solo note was saved as. */
  readonly tastingId?: string | undefined;
  readonly saved: boolean;
  /** A save that found no connection and waits for it (see outbox.ts). */
  readonly queued: boolean;
  readonly draft: NoteDraft;
  /** What the taster called before the reveal; asked only of a blind pour. */
  readonly guess: { readonly grape: string; readonly country: string };
  /** The page of the guided pager this wine was left on, 0-based. */
  readonly step: number;
  /**
   * What the taster chose to say to the group about this wine, typed on the summary. It is its own
   * field and is never filled from the note: the note's words are private.
   */
  readonly quote: string;
  /** A saved note reopened for editing: the journal entry that this edit replaces once it is saved. */
  readonly replaces?: string | undefined;
  /** When the replaced note was tasted, so editing it does not move it in the journal. */
  readonly tastedAt?: string | undefined;
}

export interface TastingRun {
  readonly id: string;
  readonly depth: NoteDepth;
  /** Labels stay hidden until the reveal: set from the start, for every wine. */
  readonly blind: boolean;
  readonly title: string;
  readonly wines: readonly RunWine[];
  /** Index of the wine in front of the taster. */
  readonly current: number;
  /** The host pressed "begin": the flight runs and its notes are taken. */
  readonly started: boolean;
  /** Whether this device makes the tasting's decisions (adds wines, begins it); a guest does not. */
  readonly host: boolean;
  /** The taster is done, but a note still waits for a connection, so the tasting stays until it is sent. */
  readonly finished: boolean;
  /**
   * Whether this taster's picks are shared live with the others (the switch in the lobby and the
   * strip). It only matters in an open, shared tasting: a blind one never shares picks.
   */
  readonly sharePicks: boolean;
  readonly flightCode?: string | undefined;
  readonly sessionId?: string | undefined;
  /** The account the tasting belongs to; absent while the taster was signed out (store.ts stamps it). */
  readonly ownerId?: string | undefined;
  /** When this copy last changed on this device (store.ts stamps it): the newest copy wins across devices. */
  readonly updatedAt: string;
  /** When the tasting began, for "started 20 minutes ago". Absent on a tasting from an older build. */
  readonly startedAt?: string | undefined;
}

export function newRun(init: { depth: NoteDepth; blind: boolean; title: string }): TastingRun {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    depth: init.depth,
    blind: init.blind,
    title: init.title,
    wines: [],
    current: 0,
    started: false,
    host: true,
    finished: false,
    sharePicks: true,
    updatedAt: now,
    startedAt: now,
  };
}

/**
 * Whether a tasting still asks something of the taster: a wine with no note sent, or a note that
 * waits for a connection. Such a tasting is offered back wherever the taster looks (the lists of
 * unfinished tastings), and is parked, not dropped, when another one starts.
 */
export function isUnfinished(run: TastingRun): boolean {
  return (!run.finished && run.wines.some((w) => !w.saved)) || run.wines.some((w) => w.queued);
}

/** The route that puts the taster back in front of their wine: the pager at its page, or the lobby. */
export function resumePath(run: TastingRun): string {
  return run.started ? `/tasting/wine/${String(run.current + 1)}` : "/tasting/lobby";
}

/**
 * What a copy of the tasting says about how far along it is, ignoring who typed what: which wine is
 * in front, each wine's page and whether it is sent. A change in it is a step; typing inside a
 * page is not, so the tasting is carried to the edge once per step and not once per tap.
 */
export function stepMark(run: TastingRun): string {
  return JSON.stringify([
    run.id,
    run.current,
    run.started,
    run.finished,
    run.flightCode ?? null,
    run.wines.map((w) => [w.key, w.step, w.saved, w.queued]),
  ]);
}

/** The catalogue's colour, with the tint its grape usually shows when we have an estimate. */
function startingDraft(depth: NoteDepth, wine: WineIdentity | undefined): NoteDraft {
  const colour = wine?.colour ?? "white";
  const draft = emptyDraft(depth, colour, wine?.sparkling ?? false);
  const hue = wine === undefined ? undefined : estimateHue(wine.grapes, colour);
  return hue === undefined ? draft : { ...draft, hue };
}

function wineEntry(
  depth: NoteDepth,
  wine: WineIdentity | undefined,
  blind: boolean,
  flightWineId?: string,
  recall?: RecallLook,
): RunWine {
  return {
    key: crypto.randomUUID(),
    wine,
    blind,
    ...(flightWineId === undefined ? {} : { flightWineId }),
    saved: false,
    queued: false,
    draft: withLook(startingDraft(depth, wine), wine === undefined ? undefined : recall?.(wine.id)),
    guess: { grape: "", country: "" },
    step: 0,
    quote: "",
  };
}

/** Puts a wine at the end of the table; the taster stays where they are. */
export function addWine(
  run: TastingRun,
  input: { wine?: WineIdentity; blind: boolean; recall?: RecallLook },
): TastingRun {
  const entry = wineEntry(run.depth, input.wine, input.blind, undefined, input.recall);
  return { ...run, wines: [...run.wines, entry] };
}

export function removeWine(run: TastingRun, key: string): TastingRun {
  const at = run.wines.findIndex((w) => w.key === key);
  if (at < 0) return run;
  const wines = run.wines.filter((w) => w.key !== key);
  const current = at < run.current ? run.current - 1 : run.current;
  return { ...run, wines, current: Math.max(0, Math.min(current, wines.length - 1)) };
}

export function selectWine(run: TastingRun, index: number): TastingRun {
  return { ...run, current: Math.max(0, Math.min(index, run.wines.length - 1)) };
}

function patchWine(run: TastingRun, key: string, patch: Partial<RunWine>): TastingRun {
  return { ...run, wines: run.wines.map((w) => (w.key === key ? { ...w, ...patch } : w)) };
}

export function patchDraft(run: TastingRun, key: string, patch: Partial<NoteDraft>): TastingRun {
  return {
    ...run,
    wines: run.wines.map((w) => (w.key === key ? { ...w, draft: { ...w.draft, ...patch } } : w)),
  };
}

export function setGuess(
  run: TastingRun,
  key: string,
  guess: Partial<{ grape: string; country: string }>,
): TastingRun {
  return {
    ...run,
    wines: run.wines.map((w) => (w.key === key ? { ...w, guess: { ...w.guess, ...guess } } : w)),
  };
}

/** Changes how deep the tasting goes; only a wine still to taste can follow it. */
export function setDepth(run: TastingRun, depth: NoteDepth): TastingRun {
  return {
    ...run,
    depth,
    wines: run.wines.map((w) => (w.saved ? w : { ...w, draft: { ...w.draft, depth } })),
  };
}

export function setSharePicks(run: TastingRun, sharePicks: boolean): TastingRun {
  return { ...run, sharePicks };
}

export function setQuote(run: TastingRun, key: string, quote: string): TastingRun {
  return patchWine(run, key, { quote });
}

export function setStep(run: TastingRun, key: string, step: number): TastingRun {
  return patchWine(run, key, { step: Math.max(0, Math.min(step, FULL_STEPS.length - 1)) });
}

export function markSaved(
  run: TastingRun,
  key: string,
  saved: { tastingId?: string | undefined },
): TastingRun {
  return patchWine(run, key, {
    saved: true,
    queued: false,
    ...(saved.tastingId === undefined ? {} : { tastingId: saved.tastingId }),
  });
}

/** Saved, but only on this device so far: the connection will carry it. */
export function markQueued(run: TastingRun, key: string): TastingRun {
  return patchWine(run, key, { saved: true, queued: true });
}

export function wineStatus(run: TastingRun, index: number): WineStatus {
  const wine = run.wines[index];
  if (wine?.saved === true) return "done";
  return index === run.current ? "tasting" : "todo";
}

export function progress(run: TastingRun): { done: number; total: number } {
  return { done: run.wines.filter((w) => w.saved).length, total: run.wines.length };
}

/** The next wine other than `from` still to taste, wrapping round; none when no other is open. */
export function nextOpen(run: TastingRun, from: number): number | undefined {
  const total = run.wines.length;
  for (let step = 1; step < total; step += 1) {
    const index = (from + step) % total;
    if (run.wines[index]?.saved === false) return index;
  }
  return undefined;
}

export function setFlight(
  run: TastingRun,
  flight: { flightCode: string; sessionId?: string | undefined },
): TastingRun {
  return {
    ...run,
    flightCode: flight.flightCode,
    ...(flight.sessionId === undefined ? {} : { sessionId: flight.sessionId }),
  };
}

/**
 * Takes the flight's wines as the table: its order, its newcomers (another device added them), what
 * is still hidden. A wine held before the flight existed is matched to its place by position, so
 * the draft written meanwhile is not lost; a wine only this device holds stays at the end.
 */
export function adoptFlight(run: TastingRun, flight: FlightState, recall?: RecallLook): TastingRun {
  const byFlightId = new Map(
    run.wines.flatMap((w) => (w.flightWineId === undefined ? [] : [[w.flightWineId, w] as const])),
  );
  const unplaced = run.wines.filter((w) => w.flightWineId === undefined);
  const used = new Set<string>();
  const wines = flight.wines
    .toSorted((a, b) => a.index - b.index)
    .map((fw): RunWine => {
      const known = byFlightId.get(fw.id) ?? unplaced.find((w) => !used.has(w.key));
      const blind = flight.blind && !fw.revealed;
      const wine = fw.wine ?? undefined;
      if (known === undefined) return wineEntry(run.depth, wine, blind, fw.id, recall);
      used.add(known.key);
      return { ...known, wine, blind, flightWineId: fw.id };
    });
  const leftovers = unplaced.filter((w) => !used.has(w.key));
  const all = [...wines, ...leftovers];
  return {
    ...run,
    title: flight.title,
    blind: flight.blind,
    flightCode: flight.joinCode,
    host: flight.isHost,
    started: flight.status !== "lobby",
    wines: all,
    current: Math.max(0, Math.min(run.current, all.length - 1)),
  };
}
